import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Shield, Clock, ArrowRight, ArrowLeft, Send } from 'lucide-react';
import { companyInfo, openingHours } from '../data';

export default function Contact() {
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll to top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update SEO Meta Tags
  useEffect(() => {
    document.title = "Kontakt Peter Sloth | Peter Sloth";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    const descriptionText = "Send os en direkte besked Du kan i nedenstående formular sende os en direkte besked. Vi bestræber os altid på at svare alle henvendelser hurtigst muligt - men giv os endeligt et kald hvis du har brug for et svar i dag.";
    if (metaDescription) {
      metaDescription.setAttribute("content", descriptionText);
    }
  }, []);

  // Form Submission Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Honeypot check
    if (data.website_url) {
      setIsSubmitting(false);
      setFormStatus({
        type: 'success',
        message: 'Tak for din henvendelse! Vi vender tilbage hurtigst muligt.',
      });
      e.target.reset();
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          service_requested: 'Generel forespørgsel (Kontakt side)',
          source_page: window.location.pathname,
        }),
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Vi har modtaget din besked og vender tilbage hurtigst muligt.',
        });
        e.target.reset();
      } else {
        throw new Error('Fejl ved indsendelse');
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Der skete en fejl. Prøv venligst igen eller ring til os direkte på tlf. 51 81 51 75.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Images from the scrape
  const contactImages = [
    "https://assets.auxodev.dk/assets/154be17d-5a98-49b2-8816-0581c018ef0c/3287a1a4-d268-467b-bc50-3ee9755214ec.jpg",
    "https://assets.auxodev.dk/assets/154be17d-5a98-49b2-8816-0581c018ef0c/db4067bd-b791-4437-aab0-ff13c80be95f.jpg",
    "https://assets.auxodev.dk/assets/154be17d-5a98-49b2-8816-0581c018ef0c/270ea1be-3ffc-40c0-8a30-eb2b069f4433.jpg"
  ];

  return (
    <div className="bg-slate-50 min-h-screen pb-16">
      {/* Hero Banner Section */}
      <section className="relative w-full text-white overflow-hidden px-4 sm:px-6 lg:px-8 z-10">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0 scale-105"
          style={{ backgroundImage: `url('${contactImages[0]}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-sky-950/95 via-blue-900/90 to-sky-950/75 z-0" />

        <div className="relative z-10 max-w-7xl mx-auto pt-44 pb-20 sm:pt-56 sm:pb-24 md:pt-64 md:pb-28 lg:pt-72 flex flex-col items-start text-left">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 font-medium mb-6">
            <Link to="/" className="hover:text-sky-400 transition-colors">Forside</Link>
            <span>/</span>
            <span className="text-white font-bold">Kontakt</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase leading-none max-w-4xl">
            Kontakt Peter Sloth
          </h1>
          <p className="mt-4 text-sm sm:text-base md:text-lg text-slate-300 max-w-2xl font-medium leading-relaxed">
            Vi bestræber os altid på at svare alle henvendelser hurtigst muligt - men giv os endeligt et kald hvis du har brug for et svar i dag.
          </p>
        </div>
      </section>

      {/* Main Content & Sidebar */}
      <section className="w-full px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start">
            
            {/* Left Column: Contact Details & Images */}
            <div className="lg:col-span-2 space-y-10 text-left">
              
              {/* Contact Info Card */}
              <div className="bg-white p-6 sm:p-10 border border-slate-200 shadow-xs">
                <h2 className="text-2xl font-extrabold text-slate-900 uppercase tracking-tight mb-6">
                  Send os en direkte besked
                </h2>
                <p className="text-slate-600 mb-8 leading-relaxed text-base sm:text-lg font-medium">
                  Du kan i nedenstående formular sende os en direkte besked. Vi bestræber os altid på at svare alle henvendelser hurtigst muligt - men giv os endeligt et kald hvis du har brug for et svar i dag.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-6 border-t border-slate-100">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="text-slate-900 pt-0.5">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900">Åstruplundvej 26, 8740 Brædstrup</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="text-slate-900 pt-0.5">
                        <Phone className="w-5 h-5" />
                      </div>
                      <div>
                        <a 
                          href={`tel:${companyInfo.phoneFormatted}`} 
                          className="block text-sm font-bold text-slate-900 hover:text-sky-500 transition-colors"
                        >
                          {companyInfo.phone}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="text-slate-900 pt-0.5">
                        <Mail className="w-5 h-5" />
                      </div>
                      <div>
                        <a 
                          href={`mailto:${companyInfo.email}`} 
                          className="block text-sm font-bold text-slate-900 hover:text-sky-500 transition-colors truncate"
                        >
                          {companyInfo.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="text-slate-900 pt-0.5">
                        <Shield className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900">{companyInfo.cvr}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Image Gallery Showcase */}
              <div className="bg-white p-6 sm:p-10 border border-slate-200 shadow-xs">
                <h2 className="text-2xl font-extrabold text-slate-900 uppercase tracking-tight mb-6">
                  Billeder fra vores projekter
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative overflow-hidden border border-slate-200 shadow-xs aspect-4/3 bg-slate-100">
                    <img
                      src={contactImages[1]}
                      alt="Peter Sloth Entreprenørarbejde"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="relative overflow-hidden border border-slate-200 shadow-xs aspect-4/3 bg-slate-100">
                    <img
                      src={contactImages[2]}
                      alt="Peter Sloth Maskiner og arbejde"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
              </div>

              {/* Back to Home Button */}
              <div className="flex justify-start">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-slate-900 hover:text-sky-500 font-bold uppercase text-xs tracking-wider transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Tilbage til forsiden
                </Link>
              </div>

            </div>

            {/* Right Column: Sidebar Form */}
            <div className="space-y-8 lg:sticky lg:top-28">
              
              {/* Contact Form */}
              <div className="bg-slate-900 text-white p-6 sm:p-8 border border-slate-800 shadow-lg">
                <h3 className="text-lg font-extrabold uppercase tracking-wider text-white mb-2">
                  Kontakt os
                </h3>
                <p className="text-xs text-slate-400 mb-6">
                  Du kan let og hurtigt sende os en besked i nedenstående formular.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4 text-left">
                  {/* Honeypot field - absolutely hidden from humans */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="website_url">Hjemmeside URL</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Navn *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Telefon *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none"
                        placeholder="Tlf. nr."
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">E-mail *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none"
                        placeholder="E-mail adresse"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Besked *</label>
                    <textarea
                      name="message"
                      required
                      rows={6}
                      className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none resize-none"
                      placeholder="Beskriv dit projekt eller stil et spørgsmål..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-slate-700 text-white font-bold uppercase text-xs tracking-wider py-3.5 px-4 transition-colors rounded-none cursor-pointer flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? 'Sender...' : 'Send besked'}
                    <Send className="w-4 h-4" />
                  </button>

                  {formStatus.message && (
                    <div className={`p-3 text-xs font-bold uppercase tracking-wider mt-4 ${
                      formStatus.type === 'success' ? 'bg-emerald-950/50 text-emerald-400 border border-emerald-800' : 'bg-rose-950/50 text-rose-400 border border-rose-800'
                    }`}>
                      {formStatus.message}
                    </div>
                  )}
                </form>
              </div>

              {/* Opening Hours */}
              <div className="bg-white p-6 sm:p-8 border border-slate-200 shadow-xs text-left">
                <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-900 mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-sky-500" />
                  <span>Åbningstider</span>
                </h3>
                <ul className="space-y-2 text-xs">
                  {openingHours.map((item, index) => (
                    <li key={index} className="flex justify-between text-slate-600 font-medium">
                      <span className="font-bold text-slate-800">{item.day}</span>
                      <span>{item.hours}</span>
                    </li>
                  ))}
                </ul>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="w-full px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white border border-slate-200 p-4 sm:p-6 shadow-xs">
            <h3 className="text-lg font-extrabold text-slate-900 uppercase tracking-tight mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-slate-900" />
              <span>Find os på kortet</span>
            </h3>
            <div className="w-full h-80 sm:h-96 md:h-[450px] relative bg-slate-100 border border-slate-200 overflow-hidden group">
              <iframe
                title="Peter Sloth - Åstruplundvej 26, 8740 Brædstrup"
                src="https://maps.google.com/maps?q=%C3%85struplundvej%2026,%208740%20Br%C3%A6dstrup&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="absolute inset-0 w-full h-full border-0 grayscale group-hover:grayscale-0 transition-all duration-500"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
              {/* Blue Overlay Tint */}
              <div className="absolute inset-0 bg-sky-900/25 pointer-events-none mix-blend-multiply group-hover:bg-transparent transition-all duration-500" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
